import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import DatalistInput from './DatalistInput';
import { API_BASE, authHeaders, inputClassName } from '../../utils/assetHelpers';

const emptyForm = {
    assetType: '',
    status: '',
    ownedBy: '',
    inCloudflare: false,
    projectCategory: '',
    name: '',
    loginID: '',
    loginPass: '',
    remarks: '',
    needPayment: false,
    paymentType: '',
    paymentCard: '',
    cardExpiryMonth: '',
    cardExpiryYear: '',
};

const OtherAssetModal = ({ mode = 'create', asset = null, meta = {}, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({ ...emptyForm });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const statuses = meta.statuses || [];
    const assetTypes = meta.other?.assetTypes || [];
    const paymentTypes = meta.other?.paymentTypes || [];
    const projectCategories = meta.other?.projectCategories || [];

    useEffect(() => {
        if (mode === 'edit' && asset) {
            setFormData({
                assetType: asset.assetType || '',
                status: asset.status || '',
                ownedBy: asset.ownedBy || '',
                inCloudflare: Boolean(asset.inCloudflare),
                projectCategory: asset.projectCategory || '',
                name: asset.name || '',
                loginID: asset.loginID || '',
                loginPass: asset.loginPass || '',
                remarks: asset.remarks || '',
                needPayment: Boolean(asset.paymentObject?.needPayment),
                paymentType: asset.paymentObject?.paymentType || '',
                paymentCard: asset.paymentObject?.paymentCard || '',
                cardExpiryMonth: asset.paymentObject?.cardExpiry?.month || '',
                cardExpiryYear: asset.paymentObject?.cardExpiry?.year || '',
            });
        }
    }, [mode, asset]);

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                assetType: formData.assetType.trim(),
                status: formData.status.trim(),
                ownedBy: formData.ownedBy.trim(),
                inCloudflare: formData.inCloudflare,
                projectCategory: formData.projectCategory.trim(),
                name: formData.name.trim(),
                loginID: formData.loginID.trim(),
                loginPass: formData.loginPass,
                remarks: formData.remarks,
                paymentObject: {
                    needPayment: formData.needPayment,
                    paymentType: formData.paymentType.trim().toLowerCase(),
                    paymentCard: formData.paymentCard.trim(),
                    cardExpiry: {
                        month: formData.cardExpiryMonth.trim(),
                        year: formData.cardExpiryYear.trim(),
                    },
                },
            };

            const request = mode === 'edit'
                ? axios.put(`${API_BASE}/api/assets/others/${asset._id}`, payload, { headers: authHeaders() })
                : axios.post(`${API_BASE}/api/assets/others`, payload, { headers: authHeaders() });

            const response = await request;
            if (response.data.success) onSuccess(response.data.data);
        } catch (err) {
            setError(`Failed to ${mode} asset: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col min-h-0 shadow-lg">
                <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-200 shrink-0">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Other Asset' : 'Create Other Asset'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
                        {error && <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <DatalistInput
                                id="other-asset-type-list"
                                label="Asset Type"
                                value={formData.assetType}
                                onChange={(value) => updateField('assetType', value)}
                                options={assetTypes}
                            />
                            <DatalistInput
                                id="other-status-list"
                                label="Status"
                                value={formData.status}
                                onChange={(value) => updateField('status', value)}
                                options={statuses}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input type="text" value={formData.name} onChange={(e) => updateField('name', e.target.value)} className={inputClassName} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Owned By</label>
                                <input type="text" value={formData.ownedBy} onChange={(e) => updateField('ownedBy', e.target.value)} className={inputClassName} />
                            </div>
                            <DatalistInput
                                id="other-project-category-list"
                                label="Project Category"
                                value={formData.projectCategory}
                                onChange={(value) => updateField('projectCategory', value)}
                                options={projectCategories}
                            />
                            <div className="flex items-center pt-6">
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input type="checkbox" checked={formData.inCloudflare} onChange={(e) => updateField('inCloudflare', e.target.checked)} className="rounded border-gray-300" />
                                    In Cloudflare
                                </label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Login ID</label>
                                <input type="text" value={formData.loginID} onChange={(e) => updateField('loginID', e.target.value)} className={inputClassName} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Login Password</label>
                                <input type="text" value={formData.loginPass} onChange={(e) => updateField('loginPass', e.target.value)} className={inputClassName} />
                            </div>
                        </div>

                        <div className="border border-gray-200 rounded-md p-4 space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-gray-800">Payment</h3>
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input type="checkbox" checked={formData.needPayment} onChange={(e) => updateField('needPayment', e.target.checked)} className="rounded border-gray-300" />
                                    Needs payment
                                </label>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DatalistInput id="other-payment-type-list" label="Payment Type" value={formData.paymentType} onChange={(value) => updateField('paymentType', value)} options={paymentTypes} />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Card</label>
                                    <input type="text" value={formData.paymentCard} onChange={(e) => updateField('paymentCard', e.target.value)} className={inputClassName} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Expiry Month</label>
                                    <input type="text" value={formData.cardExpiryMonth} onChange={(e) => updateField('cardExpiryMonth', e.target.value)} className={inputClassName} placeholder="MM" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Expiry Year</label>
                                    <input type="text" value={formData.cardExpiryYear} onChange={(e) => updateField('cardExpiryYear', e.target.value)} className={inputClassName} placeholder="YYYY" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                            <textarea value={formData.remarks} onChange={(e) => updateField('remarks', e.target.value)} rows={4} className={`${inputClassName} font-normal text-gray-800`} />
                        </div>
                    </div>

                    <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
                        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update Asset' : 'Create Asset'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OtherAssetModal;
